/**
 * An awaited `form.validateFields()` rejects with antd's `{ errorFields, values, outOfDate }`
 * when a field is invalid. The form already shows the inline error, so the rejection is expected
 * flow, not a crash.
 */
export const FORM_VALIDATION_ERROR_PATTERNS = [/Object captured as promise rejection with keys: errorFields/];

/**
 * mixpanel-browser opens IndexedDB on every init to look for unsent session recordings, even
 * with recording off, and its `onupgradeneeded` handler reads `objectStoreNames` off the
 * database the event carries. Some iOS in-app web views (WKWebView) fire that event without a
 * database, so the handler throws inside the library where nothing of ours can catch it
 * (DASHBOARD-WEB-14, /login). No config skips the check short of `disable_persistence`, which
 * would also drop mixpanel's localStorage identity. The variable name is the minifier's; no code
 * of ours touches IndexedDB.
 */
export const INDEXED_DB_UNAVAILABLE_PATTERNS = [/^undefined is not an object \(evaluating '\w+\.objectStoreNames'\)$/];

/**
 * A Redux action as Sentry records it in the breadcrumbs, or null to leave it out. The initial data carries the
 * manager and their restaurants. A platform agent chat (`platformAgent/saveChat`) carries the whole conversation,
 * the person's messages, the answers and the tool results with the restaurant's data, and a chat picked from the
 * list (`openChat`, `startChat`) its title, last line and who started it: only the type and the chat's session id
 * go, so no chat reaches Sentry and a long one can't push an error report over its size limit.
 */
export function sentryReduxAction(action) {
  if (!action?.type || action.type.startsWith('initialData/')) {
    return null;
  }

  if (action.type === 'platformAgent/saveChat') {
    return { type: action.type, payload: { session: action.payload?.session?.sessionId ?? null } };
  }

  if (action.type === 'platformAgent/openChat' || action.type === 'platformAgent/startChat') {
    return { type: action.type, payload: { id: action.payload?.summary?.id ?? null } };
  }

  return action;
}

/**
 * The Redux state attached to every Sentry event. Only UI state and the ids of the current
 * tenant: the entity slices and RTK Query caches hold owner, customer and bank details.
 */
export function sentryReduxState(state) {
  if (!state) {
    return null;
  }

  return {
    activeTab: state.activeTab,
    appStatus: state.appStatus,
    sideBar: state.sideBar,
    currentCompanyId: state.currentCompany?.company?.id ?? null,
    currentFranchiseId: state.currentFranchise?.franchise?.id ?? null,
    currentServiceProviderId: state.currentServiceProvider?.serviceProvider?.id ?? null,
  };
}

const STACK_OVERFLOW_MESSAGE = /Maximum call stack size exceeded|too much recursion/;

/** Our code ships as Vite chunks under /assets/; anything else on a stack is the browser, a library CDN or an injected script. */
function isOwnFrame(frame) {
  return /\/assets\/[^/]+\.js/.test(frame?.filename ?? '');
}

/** The same call site of ours on the stack three or more times: a cycle in our code. */
function ownFramesRecurse(frames) {
  const counts = new Map();

  return frames.filter(isOwnFrame).some((frame) => {
    const key = `${frame.filename}:${frame.lineno}:${frame.colno}`;
    counts.set(key, (counts.get(key) ?? 0) + 1);

    return counts.get(key) >= 3;
  });
}

/**
 * Sentry `beforeSend`. Drops a stack overflow ("Maximum call stack size exceeded", Firefox's
 * "too much recursion") unless our own frames repeat. The browser can run out of stack inside a
 * date parse or an injected script with nothing recursive of ours on it: Firefox 159 does so in
 * moment/dayjs parsing (SHOP-WEB-5D / -6Q), and Chrome for iOS's own content scripts do so with no
 * frame of ours at all (DASHBOARD-WEB-1A, OMS-WEB-A). A real infinite recursion repeats our own
 * call sites (SHOP-WEB-5B), so that one still reports.
 */
export function beforeSendError(event) {
  const isStackOverflowNoise = event?.exception?.values?.some(
    (value) => STACK_OVERFLOW_MESSAGE.test(value.value ?? '') && !ownFramesRecurse(value.stacktrace?.frames ?? []),
  );

  return isStackOverflowNoise ? null : event;
}
